import { useState, useEffect } from 'react'
import { getErrorLogs, clearErrorLogs, downloadErrorLogs } from '../lib/errorLogger.js'

export default function DebugPanel() {
    const [isOpen, setIsOpen] = useState(false)
    const [logs, setLogs] = useState([])
    const [reactErrors, setReactErrors] = useState([])
    const [tab, setTab] = useState('errors') // 'errors' or 'react'

    useEffect(() => {
        if (isOpen) {
            refreshLogs()
        }
    }, [isOpen])

    const refreshLogs = () => {
        setLogs(getErrorLogs())
        try {
            const reactErrorsData = JSON.parse(localStorage.getItem('react_error_logs') || '[]')
            setReactErrors(reactErrorsData)
        } catch {
            setReactErrors([])
        }
    }

    const handleClear = () => {
        clearErrorLogs()
        refreshLogs()
    }

    const handleDownload = () => {
        downloadErrorLogs()
    }

    const totalErrors = logs.length + reactErrors.length

    // Hidden button in bottom right corner - triple tap to activate
    const [tapCount, setTapCount] = useState(0)
    const handleTap = () => {
        setTapCount(prev => prev + 1)
        setTimeout(() => setTapCount(0), 1000)
    }

    useEffect(() => {
        if (tapCount >= 3) {
            setIsOpen(true)
            setTapCount(0)
        }
    }, [tapCount])

    if (!isOpen) {
        return (
            <button
                onClick={handleTap}
                className="fixed bottom-4 right-4 h-12 w-12 rounded-full bg-purple-600/20 text-xs text-white opacity-30 hover:opacity-100"
                title="Debug Panel (tap 3x)"
            >
                {totalErrors > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold">
                        {totalErrors}
                    </span>
                )}
                🐛
            </button>
        )
    }

    const currentLogs = tab === 'errors' ? logs : reactErrors

    return (
        <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm">
            <div className="flex h-full flex-col bg-white md:mx-auto md:my-8 md:max-w-4xl md:rounded-lg md:shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 p-4">
                    <h2 className="text-lg font-bold text-slate-800">Debug Panel</h2>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="rounded-lg bg-slate-200 px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-300"
                    >
                        Close
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-slate-200 bg-white">
                    <button
                        onClick={() => setTab('errors')}
                        className={`flex-1 px-4 py-3 text-sm font-medium ${
                            tab === 'errors'
                                ? 'border-b-2 border-purple-600 text-purple-600'
                                : 'text-slate-600 hover:text-slate-800'
                        }`}
                    >
                        App Errors ({logs.length})
                    </button>
                    <button
                        onClick={() => setTab('react')}
                        className={`flex-1 px-4 py-3 text-sm font-medium ${
                            tab === 'react'
                                ? 'border-b-2 border-purple-600 text-purple-600'
                                : 'text-slate-600 hover:text-slate-800'
                        }`}
                    >
                        React Errors ({reactErrors.length})
                    </button>
                </div>

                {/* Actions */}
                <div className="flex gap-2 border-b border-slate-200 bg-slate-50 p-3">
                    <button
                        onClick={refreshLogs}
                        className="rounded bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700"
                    >
                        Refresh
                    </button>
                    <button
                        onClick={handleDownload}
                        className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                        Download
                    </button>
                    <button
                        onClick={handleClear}
                        className="rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
                    >
                        Clear All
                    </button>
                </div>

                {/* Logs */}
                <div className="flex-1 overflow-auto p-4">
                    {currentLogs.length === 0 ? (
                        <div className="flex h-full items-center justify-center text-slate-500">
                            No {tab === 'errors' ? 'app' : 'React'} errors logged
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {currentLogs.map((log, index) => (
                                <div
                                    key={index}
                                    className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm"
                                >
                                    <div className="mb-2 flex items-start justify-between">
                                        <span className="rounded bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
                                            {log.type || 'ERROR'}
                                        </span>
                                        <span className="text-xs text-slate-500">
                                            {new Date(log.timestamp).toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="mb-2 font-semibold text-red-800">
                                        {log.message}
                                    </div>
                                    <details className="text-xs">
                                        <summary className="cursor-pointer font-medium text-slate-700">
                                            Details
                                        </summary>
                                        <pre className="mt-2 max-h-40 overflow-auto rounded bg-white p-2 text-xs text-slate-600">
                                            {JSON.stringify(log, null, 2)}
                                        </pre>
                                    </details>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
